import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import topImage from '../icon/top_pic.jpg';
import ScrollToTop from '../components/ScrollToTop';

interface Job {
  id: number;
  title: string;
  title_en?: string;
  location: string;
  location_en?: string;
  responsibilities: string;
  responsibilities_en?: string;
  qualifications: string;
  qualifications_en?: string;
  is_active: boolean;
  published_at: string;
  created_at: string;
  updated_at: string;
}

const Join: React.FC = () => {
  const { i18n } = useTranslation();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);

  // 获取职位数据
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/jobs?activeOnly=true');
        setJobs(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
        setError('无法加载职位数据');
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const getLocalizedText = (zh: string, en: string) => {
    return i18n.language === 'en' ? en : zh;
  };

  // 获取本地化职位字段
  const getLocalizedJobField = (job: Job | null, zhField: keyof Job, enField: keyof Job) => {
    if (!job) return '';
    return i18n.language === 'en' ? (job[enField] || job[zhField] || '') : (job[zhField] || job[enField] || '');
  };

  // 格式化日期
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${getLocalizedText('发布日期', 'Published Date')}: ${date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'zh-CN')}`;
  };

  // 展开/收起职位详情
  const handleToggleJobDetails = (jobId: number) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  // 渲染HTML内容（处理换行符）
  const renderContent = (content: string) => {
    // 将换行符替换为<br>标签
    return { __html: content.replace(/\n/g, '<br>') };
  };

  return (
    <div className="pt-[80px] min-h-screen bg-gray-50">
      <ScrollToTop />
      {/* 顶部图片 */}
      <div className="w-full">
        <img src={topImage} alt="Join Banner" className="w-full h-[630px] object-cover" />
      </div>

      {/* 内容区域 */}
      <div className="mx-auto px-4 md:px-12 py-12 md:py-20 max-w-[2000px]">
        {/* 标题区域 */}
        <div className="relative mb-12 md:mb-24">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold">{getLocalizedText('加入我们', 'Join Us')}</h2>
          </div>
          <h3 className="text-lg md:text-xl font-medium mt-2">{getLocalizedText('招贤纳士', 'Recruitment')}</h3>
          <div className="absolute -bottom-4 md:-bottom-6 left-0 right-0 h-[1px] bg-gray-300" />
        </div>

        {/* 职位列表 */}
        <div className="mb-12">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-center">
              {error}
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8 border-t border-gray-200">
              <p className="text-gray-500">
                {getLocalizedText('当前没有开放的职位', 'No open positions at the moment')}
              </p>
            </div>
          ) : (
            <div>
              {jobs.map((job) => (
                <div key={job.id} className="border-b border-gray-200 py-4">
                  {/* 职位标题区域 - 始终可见 */}
                  <div 
                    className="cursor-pointer"
                    onClick={() => handleToggleJobDetails(job.id)}
                  >
                    {/* 在PC端使用三列网格布局，在移动端使用纵向布局 */}
                    <div className="flex flex-col md:grid md:grid-cols-3 md:items-center">
                      <h3 className="text-xl font-medium">{getLocalizedJobField(job, 'title', 'title_en')}</h3>
                      <div className="text-gray-500 mt-1 md:mt-0 md:text-center">{formatDate(job.published_at)}</div>
                      <div className="mt-2 md:mt-0 text-right">
                        <span className="text-blue-600 hover:underline hidden md:inline">
                          {expandedJobId === job.id 
                            ? getLocalizedText('收起详情', 'Close Details') 
                            : getLocalizedText('展开详情', 'View Details')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 职位详情区域 - 展开时可见 */}
                  {expandedJobId === job.id && (
                    <div className="mt-4 pl-4 border-l-2 border-gray-300">
                      <div className="mb-6">
                        <h4 className="font-semibold mb-2">{getLocalizedText('岗位职责:', 'Job Responsibilities:')}</h4>
                        <div 
                          className="text-gray-700 whitespace-pre-line" 
                          dangerouslySetInnerHTML={renderContent(getLocalizedJobField(job, 'responsibilities', 'responsibilities_en') as string)}
                        ></div>
                      </div>

                      <div className="mb-6">
                        <h4 className="font-semibold mb-2">{getLocalizedText('任职资格:', 'Qualifications:')}</h4>
                        <div 
                          className="text-gray-700 whitespace-pre-line" 
                          dangerouslySetInnerHTML={renderContent(getLocalizedJobField(job, 'qualifications', 'qualifications_en') as string)}
                        ></div>
                      </div>

                      <div className="mt-4">
                        <p className="text-gray-600 mb-2">
                          {getLocalizedText(
                            '请将您的简历发送至以下邮箱，邮件主题请注明应聘职位名称：',
                            'Please send your resume to the following email address with the position name in the subject line:'
                          )}
                        </p>
                        <div className="text-blue-600">wang.jun@ddi-arch.com</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 招聘联系 */}
        <div className="my-12 pt-6">
          <h2 className="text-2xl font-bold mb-3">
            {getLocalizedText('招聘联系', 'Contact Us')}
          </h2>
          <div className="h-[1px] bg-gray-300 mb-6"></div>
          
          <div>
            <p className="text-gray-700 mb-2">
              <span className="font-semibold">{getLocalizedText('招聘咨询：', 'Recruitment Contact:')}</span>
              0755-82514567 {getLocalizedText('人力资源部王经理', 'HR Manager Wang')}
            </p>
            <p className="text-gray-700 mb-2">
              <span className="font-semibold">{getLocalizedText('单位地址：', 'Address:')}</span>
              {getLocalizedText(
                '深圳市南山区科技园南区粤兴五道9号北理工创新大厦1601室',
                'Room 1601, BIT Innovation Building, No.9 Yuexing 5th Road, South Area of Science Park, Nanshan District, Shenzhen'
              )}
            </p>
            <p className="text-gray-700 mb-2">
              <span className="font-semibold">{getLocalizedText('邮 编：', 'Postal Code:')}</span>
              518000
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Join; 